import { Check, OpenInNew } from "@mui/icons-material";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { FormControlLabel, GlobalStyles, ThemeProvider } from "@mui/material";
import ZiplyLogoIcon from "../../assets/images/ziply-logo.png";
import { Footer } from "../../components/shared/Footer/Footer";
import { SEO } from "../../components/shared/SEO/SEO";
import { CONTACT_LINK, FIBERFIRST_LOGIN_CONSTANTS } from "../../Constants/constants";
import { useLoginForm } from "../../hooks/Login/useLoginForm";
import { ErrorStack, ErrorTypography } from "../../Styles/GlobalStyles";
import {
  BrandLogo,
  ContentGrid,
  FiberFirstPage,
  FiberFirstTextField,
  ForgotPasswordLink,
  ForgotPasswordRow,
  FormCard,
  FormDivider,
  GOTHAM_FONT_FACES,
  HeaderBar,
  HeaderInner,
  HelpCenterLink,
  HelpRow,
  HeroBody,
  HeroSection,
  HeroSubtitle,
  HeroTitle,
  InfoBody,
  InfoBox,
  InfoHeading,
  KeepLoggedInRow,
  KeepLoggedInSwitch,
  LoginButton,
  MainContent,
  SwitchThumb,
  withGothamFont,
} from "./FiberFirstLoginStyles";
import { LoginDialogs } from "./LoginDialogs";

// Login's relative "enterpriseportal/" resolves to /myaccount/enterpriseportal/ from
// /myaccount/login; this page is one level deeper, so it needs the absolute path.
const ENTERPRISE_PORTAL_URL = "/myaccount/enterpriseportal/";
const ZIPLY_HOME_URL = "https://ziplyfiber.com";

// Landing login for migrated FiberFirst customers. Same login flow as /login.
export default function FiberFirstLogin({ setUser }: any) {
  const login = useLoginForm({ setUser, enterprisePortalUrl: ENTERPRISE_PORTAL_URL });
  const {
    email,
    password,
    emailError,
    passwordError,
    errorMsg,
    checked,
    handleEmailInput,
    handlePasswordInput,
    checkBoxHandler,
    checkValidation,
    onEnterSubmit,
    handleForgotPasswordRedirect,
  } = login;

  // Gotham is licensed for the public login page only, so it is scoped here, not app-wide
  return (
    <ThemeProvider theme={withGothamFont}>
      <GlobalStyles styles={GOTHAM_FONT_FACES} />
      <SEO
        title={FIBERFIRST_LOGIN_CONSTANTS.SEO_TITLE}
        description={FIBERFIRST_LOGIN_CONSTANTS.SEO_DESCRIPTION}
      />
      <FiberFirstPage>
        <HeaderBar>
          <HeaderInner>
            <a href={ZIPLY_HOME_URL} target="_self">
              <BrandLogo src={ZiplyLogoIcon} alt={FIBERFIRST_LOGIN_CONSTANTS.LOGO_ALT} />
            </a>
          </HeaderInner>
        </HeaderBar>

        <MainContent>
          <ContentGrid>
            {/* ─── Hero ─── */}
            <HeroSection>
              <HeroTitle variant="h1">
                {FIBERFIRST_LOGIN_CONSTANTS.HEADING_PREFIX}{" "}
                <span>{FIBERFIRST_LOGIN_CONSTANTS.HEADING_BRAND}</span>
              </HeroTitle>
              <HeroSubtitle variant="h2">{FIBERFIRST_LOGIN_CONSTANTS.SUBHEADING}</HeroSubtitle>
              <HeroBody>{FIBERFIRST_LOGIN_CONSTANTS.DESCRIPTION}</HeroBody>
            </HeroSection>

            {/* ─── Login Form ─── */}
            <div>
              <FormCard>
                <FiberFirstTextField
                  id="fiberfirst-login-email"
                  label={FIBERFIRST_LOGIN_CONSTANTS.EMAIL_LABEL}
                  inputProps={{ inputMode: "email" }}
                  autoComplete="email"
                  value={email}
                  error={!!emailError}
                  helperText={emailError}
                  onChange={(e: any) => handleEmailInput(e.target.value)}
                  onKeyDown={onEnterSubmit}
                />
                <FiberFirstTextField
                  id="fiberfirst-login-password"
                  label={FIBERFIRST_LOGIN_CONSTANTS.PASSWORD_LABEL}
                  isPassword
                  autoComplete="current-password"
                  value={password}
                  error={!!passwordError}
                  helperText={passwordError}
                  onChange={(e: any) => handlePasswordInput(e)}
                  onKeyDown={onEnterSubmit}
                />
                <ForgotPasswordRow>
                  <ForgotPasswordLink
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={handleForgotPasswordRedirect}>
                    {FIBERFIRST_LOGIN_CONSTANTS.FORGOT_PASSWORD}
                  </ForgotPasswordLink>
                </ForgotPasswordRow>

                {errorMsg && (
                  <ErrorStack mt={2}>
                    <CancelOutlinedIcon color="error" fontSize="small" sx={{ ml: 1 }} />
                    <ErrorTypography>{errorMsg}</ErrorTypography>
                  </ErrorStack>
                )}

                <KeepLoggedInRow>
                  <FormControlLabel
                    control={
                      <KeepLoggedInSwitch
                        checked={checked}
                        onChange={checkBoxHandler}
                        disableRipple
                        icon={<SwitchThumb />}
                        checkedIcon={
                          <SwitchThumb>
                            <Check sx={{ fontSize: 16 }} />
                          </SwitchThumb>
                        }
                      />
                    }
                    label={FIBERFIRST_LOGIN_CONSTANTS.KEEP_ME_LOGGED_IN}
                  />
                </KeepLoggedInRow>

                <LoginButton variant="contained" fullWidth onClick={(e) => checkValidation(e)}>
                  {FIBERFIRST_LOGIN_CONSTANTS.LOGIN_BUTTON}
                </LoginButton>

                <FormDivider />

                <InfoBox>
                  <InfoHeading variant="h3">
                    {FIBERFIRST_LOGIN_CONSTANTS.FIRST_TIME_HEADING}
                  </InfoHeading>
                  <InfoBody>
                    {FIBERFIRST_LOGIN_CONSTANTS.FIRST_TIME_PREFIX}{" "}
                    <strong>{FIBERFIRST_LOGIN_CONSTANTS.FIRST_TIME_ACTION}</strong>{" "}
                    {FIBERFIRST_LOGIN_CONSTANTS.FIRST_TIME_SUFFIX}
                  </InfoBody>
                </InfoBox>
              </FormCard>

              <HelpRow>
                <span>{FIBERFIRST_LOGIN_CONSTANTS.HELP_TEXT}</span>
                <HelpCenterLink
                  href={CONTACT_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover">
                  {FIBERFIRST_LOGIN_CONSTANTS.HELP_CENTER_LINK}
                  <OpenInNew sx={{ fontSize: 20 }} />
                </HelpCenterLink>
              </HelpRow>
            </div>
          </ContentGrid>
        </MainContent>

        <Footer />
      </FiberFirstPage>
      <LoginDialogs login={login} />
    </ThemeProvider>
  );
}
